import { useEffect, useRef, useState } from 'react'

import { EyeOff, ScanEye } from 'lucide-react'
import { SubmitHandler, UseFormReturn, useWatch } from 'react-hook-form'

import { getT, toISO8601 } from '@fortem-monorepo/js-utilities'
import { useUserContext } from '@fortem-monorepo/react-spa'
import { Button } from '@fortem-monorepo/react-ui'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  DialogTitle,
} from '@fortem-monorepo/react-ui'
import { SubmitButton } from '@fortem-monorepo/react-ui'

import {
  RunMarketDataJobsForm,
  toRunMarketdataJobParams,
} from '@/components/forms/RunMarketDataJobsForm/RunMarketDataJobsForm'
import { SchemaType } from '@/components/forms/RunMarketDataJobsForm/RunMarketDataJobsForm.schema'
import { RunMarketDataJobsFormFields } from '@/components/forms/RunMarketDataJobsForm/RunMarketDataJobsFormFields'
import { FormValues } from '@/pages/funds/ic-guidelines-v2/components/edit-guideline-form/FormValues'
import { useRunMarketdataJobMutation } from '@/react-query/mutations/marketdata'

export interface RunMarketDataJobsModalProps extends DialogProps {}

export const RunMarketDataJobsModal: React.FC<RunMarketDataJobsModalProps> = ({ ...props }) => {
  const [formValuesOpen, setFormValuesOpen] = useState(false)
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const { isDeveloper } = useUserContext()
  const runMarketdataJobMutation = useRunMarketdataJobMutation()

  const handleSubmit: SubmitHandler<SchemaType> = async (data) => {
    await runMarketdataJobMutation.mutateAsync(toRunMarketdataJobParams(data))
    setConfirmationOpen(false)
    props.onOpenChange?.(false)
  }

  const close = () => {
    setConfirmationOpen(false)
    props.onOpenChange?.(false)
  }

  return (
    <Dialog {...props}>
      <DialogContent className={'max-w-150!'}>
        <RunMarketDataJobsForm
          defaultValues={{
            value_date: toISO8601(getT()),
            chosen_jobs: [],
            force_historical: false,
            only_tickers: [],
            skip_closed_european: false,
          }}
          onSubmit={handleSubmit}
        >
          {(form) => {
            const {
              formState: { isValid, isSubmitting },
            } = form

            return (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2">
                    <DialogTitle>Run Marketdata Jobs</DialogTitle>
                    {isDeveloper() && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setFormValuesOpen((prev) => !prev)}
                      >
                        {!formValuesOpen ? <ScanEye /> : <EyeOff />}
                      </Button>
                    )}
                  </div>
                </DialogHeader>
                <div className="py-6">
                  {formValuesOpen && (
                    <div className={'mb-4'}>
                      <FormValues />
                    </div>
                  )}
                  <RunMarketDataJobsFormFields />
                  <CloseConfirmationOnFormChange onChange={() => setConfirmationOpen(false)} />
                </div>
                {confirmationOpen && <RunMarketDataJobsConfirmation form={form} />}
                <DialogFooter>
                  {confirmationOpen ? (
                    <SubmitButton
                      disabled={!isValid || isSubmitting}
                      loading={isSubmitting}
                      size="sm"
                    >
                      Confirm Run
                    </SubmitButton>
                  ) : (
                    <Button
                      type="button"
                      disabled={!isValid || isSubmitting}
                      size="sm"
                      onClick={() => setConfirmationOpen(true)}
                    >
                      Run
                    </Button>
                  )}
                  <Button type="button" variant="destructive" size="sm" onClick={close}>
                    Cancel
                  </Button>
                </DialogFooter>
              </>
            )
          }}
        </RunMarketDataJobsForm>
      </DialogContent>
    </Dialog>
  )
}

interface CloseConfirmationOnFormChangeProps {
  onChange: () => void
}

const CloseConfirmationOnFormChange: React.FC<CloseConfirmationOnFormChangeProps> = ({
  onChange,
}) => {
  const values = useWatch<SchemaType>()
  const firstRender = useRef(true)

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }

    onChange()
  }, [values])

  return null
}

interface RunMarketDataJobsConfirmationProps {
  form: UseFormReturn<SchemaType>
}

const RunMarketDataJobsConfirmation: React.FC<RunMarketDataJobsConfirmationProps> = ({ form }) => {
  const values = form.watch()
  const jobCount = values.chosen_jobs.length

  return (
    <div className="mb-4 rounded-md border border-red-600 p-3 text-sm font-medium text-red-600">
      {jobCount > 0
        ? `Warning: You are about to queue ${jobCount} marketdata job(s) for ${values.value_date}.`
        : `Warning: You are about to queue the backend default marketdata jobs for ${values.value_date}.`}
    </div>
  )
}
